// Regras puras de exibição dos rastreadores GPS (sem React, sem API).

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

/** "há 3 min", a partir da idade em segundos da última comunicação. */
export function formatAgo(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return '—';
  if (seconds < 60) return 'agora';
  if (seconds < 3600) return `há ${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `há ${Math.floor(seconds / 3600)} h`;
  const days = Math.floor(seconds / 86400);
  return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
}

/**
 * Situação da comunicação. O TK915 dorme quando o veículo para e deixa de
 * enviar, então algumas horas de silêncio são normais; mais de um dia não.
 */
export function commStatus(seconds: number | null | undefined): { label: string; variant: BadgeVariant } {
  if (seconds == null) return { label: 'Nunca comunicou', variant: 'neutral' };
  if (seconds <= 300) return { label: 'Comunicando', variant: 'success' };
  if (seconds <= 86400) return { label: 'Em silêncio', variant: 'warning' };
  return { label: 'Sem comunicação', variant: 'danger' };
}

/** Padrões dos limites de bateria (NULL no cadastro) — os mesmos do ERPApi e do FleetTrackerGo. */
export const DEFAULT_BATTERY_LOW = 30;
export const DEFAULT_BATTERY_CRITICAL = 15;
export const DEFAULT_BATTERY_DEAD_SILENCE_MIN = 15;

/** Cor da bateria pelos limites do rastreador: até o crítico vermelha, até o aviso amarela. */
export function batteryVariant(
  percent: number | null | undefined,
  low: number = DEFAULT_BATTERY_LOW,
  critical: number = DEFAULT_BATTERY_CRITICAL,
): BadgeVariant {
  if (percent == null) return 'neutral';
  if (percent <= critical) return 'danger';
  if (percent <= low) return 'warning';
  return 'success';
}

const BATTERY_STATES: Record<string, { label: string; variant: BadgeVariant }> = {
  charging: { label: 'Carregando (veículo ligado)', variant: 'success' },
  discharging: { label: 'Descarregando (veículo desligado)', variant: 'info' },
  silent: { label: 'Sem comunicação', variant: 'danger' },
  unknown: { label: 'Sem leituras', variant: 'neutral' },
};

export function batteryStateLabel(state: string): { label: string; variant: BadgeVariant } {
  return BATTERY_STATES[state] ?? BATTERY_STATES.unknown;
}

const CYCLE_END_LABELS: Record<string, string> = {
  charging: 'Voltou a carregar',
  silent: 'Parou de comunicar',
  ongoing: 'Em andamento',
};

export function cycleEndLabel(endedBy: string): string {
  return CYCLE_END_LABELS[endedBy] ?? endedBy;
}

/** "45 min", "12,5 h", "9,4 dias" — duração em horas, em pt-BR. */
export function formatHours(hours: number | null | undefined): string {
  if (hours == null || hours < 0) return '—';
  const br = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${br(hours)} h`;
  return `${br(hours / 24)} dias`;
}

/** "0,33%/h" */
export function formatRate(rate: number | null | undefined): string {
  if (rate == null) return '—';
  return `${rate.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%/h`;
}

const BATTERY_ALERT_TEXT: Record<string, string> = {
  low: 'Bateria baixa',
  critical: 'Bateria crítica',
  silent: 'Provavelmente desligou (bateria) — religar no botão',
};

export function batteryAlertText(level: string): string {
  return BATTERY_ALERT_TEXT[level] ?? level;
}

const EVENT_LABELS: Record<string, string> = {
  low_battery: 'Bateria fraca (alarme do aparelho)',
  battery_low: 'Bateria abaixo do aviso',
  battery_critical: 'Bateria crítica',
  charging: 'Carregando (veículo ligado)',
  battery_dead: 'Desligou por bateria',
  removal: 'Remoção do aparelho',
  sos: 'SOS',
  power_cut: 'Corte de alimentação',
  geofence_exit: 'Saída de cerca',
  geofence_enter: 'Entrada em cerca',
  fall: 'Queda',
  alarm_unknown: 'Alarme não identificado',
  connected: 'Conectou',
  disconnected: 'Desconectou',
  invalid_time: 'Horário inválido no aparelho',
};

export function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type;
}

export function eventVariant(type: string): BadgeVariant {
  if (type === 'connected' || type === 'disconnected') return 'neutral';
  if (type === 'charging') return 'success';
  if (type === 'invalid_time' || type === 'geofence_exit' || type === 'geofence_enter' || type === 'battery_low') return 'warning';
  return 'danger';
}

const RAW_TYPE_LABELS: Record<string, string> = {
  UD: 'Posição',
  UD2: 'Posição (reenvio)',
  AL: 'Alarme',
  LK: 'Sinal de vida',
  INIT: 'Inicialização',
  DESCONHECIDO: 'Fora do protocolo',
};

/** Nome amigável do tipo de mensagem; tipo ainda não mapeado aparece como veio. */
export function rawTypeLabel(type: string | null | undefined): string {
  if (!type) return 'Ilegível';
  return RAW_TYPE_LABELS[type] ?? type;
}

/** Link do Google Maps para uma coordenada. */
export function mapsLink(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}
